import React, { useState, useEffect } from "react";
import { 
    FaMoneyBillWave, FaFilePdf, FaSearch, FaTrash, FaCheck, FaEdit, FaUniversity, FaTimes, FaHistory, FaEye
} from 'react-icons/fa';
import { 
    fetchFactures, createEncaissement, fetchEncaissements, 
    exportEncaissementPDF, createDecaissementSelection, 
    fetchClients, deleteEncaissement, updateEncaissement,
    createAvoir, fetchAvoirs 
} from "../api";

const EncaissementPage = ({ user }) => {
    // --- IDENTIFICATION ET SÉCURITÉ ---
    const userEmail = user?.email?.toLowerCase() || '';
    const userRole = user?.role?.toLowerCase() || '';

    const isAdmin = userRole === 'admin' || userRole === 'superadmin';
    const isKhKoffi = userEmail === 'kh.koffi@arsn.ci' || userEmail === 'kh.koffi@gmail.com';
    const isKbOuattara = userEmail === 'kb.ouattara@arsn.ci' || userEmail === 'kb.ouattara@gmail.com';
    
    const canEditAnywhere = isAdmin || isKhKoffi;

    // --- DONNÉES ---
    const [allFacturesRaw, setAllFacturesRaw] = useState([]); 
    const [allFactures, setAllFactures] = useState([]);
    const [allAvoirs, setAllAvoirs] = useState([]); 
    const [allClientsObj, setAllClientsObj] = useState([]); 
    const [history, setHistory] = useState([]); 
    const [clientsWithDebtNames, setClientsWithDebtNames] = useState([]); 

    // --- FORMULAIRE ---
    const [editingId, setEditingId] = useState(null);
    const [clientSearch, setClientSearch] = useState("");
    const [selectedClient, setSelectedClient] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [availableFactures, setAvailableFactures] = useState([]); 
    const [selectedFactureIds, setSelectedFactureIds] = useState([]); 
    const [isDetteInitialeSelected, setIsDetteInitialeSelected] = useState(false); 
    const [oldFactureReferences, setOldFactureReferences] = useState(""); // NOUVEAU
    const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split('T')[0]);
    const [prestationManuelle, setPrestationManuelle] = useState(""); 
    const [paymentMode, setPaymentMode] = useState("Espèces");
    const [paymentReference, setPaymentReference] = useState(""); 
    const [amountToPay, setAmountToPay] = useState(0);
    const [proofFile, setProofFile] = useState(null);

    // --- MODALS ---
    const [showDecaisserModal, setShowDecaisserModal] = useState(false);
    const [decaisserData, setDecaisserData] = useState({ 
        id: null, nature: "", reference: "", date: new Date().toISOString().split('T')[0] 
    });
    const [detailsModalData, setDetailsModalData] = useState(null);

    // --- UI ---
    const [message, setMessage] = useState(null);
    const [searchHistory, setSearchHistory] = useState("");
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);

    const getFactureTotal = (f) => {
        return f.totalGeneral || f.totalTTC || (f.lines ? f.lines.reduce((acc, l) => acc + (l.total || 0), 0) : 0);
    };

    const loadData = async () => {
        try {
            const [resEnc, resCli, resFac, resAvr] = await Promise.all([
                fetchEncaissements(), fetchClients(), fetchFactures(), fetchAvoirs() 
            ]);
            
            setHistory(resEnc.data || []);
            setAllClientsObj(resCli.data || []); 
            setAllFacturesRaw(resFac.data || []); 
            
            const openFactures = (resFac.data || []).filter(f => {
                const status = f.status?.toLowerCase() || '';
                const total = getFactureTotal(f);
                const resteAPayer = total - (f.amountPaid || 0);
                return status !== 'payée' && status !== 'annulée' && resteAPayer > 0;
            });
            
            setAllFactures(openFactures);
            setAllAvoirs(resAvr.data || []); 

            const names = [...new Set([
                ...openFactures.map(f => f.clientName),
                ...(resCli.data || []).filter(c => c.initialDebt > 0).map(c => c.name)
            ])].sort();
            setClientsWithDebtNames(names);
        } catch (e) { showMessage("error", "Erreur de chargement des données."); }
    };

    useEffect(() => { loadData(); }, []);

    const handlePaymentModeChange = (e) => {
        const mode = e.target.value;
        setPaymentMode(mode);
        setPaymentReference(""); 
    };

    const handleSelectClient = (name) => {
        setSelectedClient(name);
        setClientSearch(name);
        setShowDropdown(false);
        const filtered = allFactures.filter(f => f.clientName?.toLowerCase().trim() === name.toLowerCase().trim());
        setAvailableFactures(filtered);
        setSelectedFactureIds([]);
        setIsDetteInitialeSelected(false);
        setOldFactureReferences("");
        setAmountToPay(0);
        setPaymentReference(""); 
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setClientSearch(val);
        setShowDropdown(true);

        const exactMatch = clientsWithDebtNames.find(n => n.toLowerCase().trim() === val.toLowerCase().trim());
        if (exactMatch) {
            handleSelectClient(exactMatch);
        } else {
            setSelectedClient("");
            setAvailableFactures([]);
        }
    };

    const calculateSuggestedAmount = (factureIds, detteSelected) => {
        let total = 0;
        if (detteSelected) {
            const client = allClientsObj.find(c => c.name === selectedClient);
            total += client ? client.initialDebt : 0;
        }
        total += availableFactures
            .filter(fac => factureIds.includes(fac._id))
            .reduce((sum, fac) => sum + (getFactureTotal(fac) - (fac.amountPaid || 0)), 0);
        return total;
    };

    const toggleFacture = (f) => {
        const id = f._id;
        const newSelection = selectedFactureIds.includes(id) 
            ? selectedFactureIds.filter(fid => fid !== id) 
            : [...selectedFactureIds, id];
        
        setSelectedFactureIds(newSelection);
        
        if (!editingId) {
            setAmountToPay(calculateSuggestedAmount(newSelection, isDetteInitialeSelected));
        }
    };

    const toggleDetteInitiale = () => {
        const newState = !isDetteInitialeSelected;
        setIsDetteInitialeSelected(newState);
        
        if (!newState) setOldFactureReferences("");
        if (!editingId) {
            setAmountToPay(calculateSuggestedAmount(selectedFactureIds, newState));
        }
    };

    const handleDownloadPDF = async (id, number) => {
        setDownloadingPdfId(id);
        try {
            const res = await exportEncaissementPDF(id);
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `RECU_${number.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) { 
            showMessage("error", "Erreur lors de la génération du PDF."); 
        } finally {
            setDownloadingPdfId(null);
        }
    };

    const showMessage = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

    const resetForm = () => {
        setEditingId(null); setSelectedClient(""); setClientSearch(""); setSelectedFactureIds([]); 
        setIsDetteInitialeSelected(false); setOldFactureReferences(""); setPrestationManuelle(""); setAmountToPay(0);
        setPaymentReference(""); setPaymentMode("Espèces"); setProofFile(null);
    };

    const startEdit = (e) => {
        if (!canEditAnywhere) {
            showMessage("error", "Accès refusé : Vous n'avez pas les droits de modification.");
            return;
        }

        setEditingId(e._id);
        setSelectedClient(e.clientName);
        setClientSearch(e.clientName);
        setAmountToPay(e.totalAmount);
        setPaymentMode(e.paymentMode);
        setPaymentReference(e.paymentReference || "");
        setPrestationManuelle(e.prestationManuelle || "");
        setReceiptDate(new Date(e.dateEncaissement || e.date).toISOString().split('T')[0]);
        setIsDetteInitialeSelected(e.isDetteInitiale || false);
        setOldFactureReferences(e.oldFactureReferences || "");
        
        const receiptInvoices = allFacturesRaw.filter(f => (e.factureNumbers || []).includes(f.number));
        setSelectedFactureIds(receiptInvoices.map(f => f._id));
        
        const openForClient = allFactures.filter(f => f.clientName?.toLowerCase().trim() === e.clientName?.toLowerCase().trim());
        const merged = [...openForClient];
        
        receiptInvoices.forEach(ri => {
            if (!merged.find(m => m._id === ri._id)) {
                merged.push(ri);
            }
        });

        setAvailableFactures(merged);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!canEditAnywhere) {
            showMessage("error", "Accès refusé : Vous n'avez pas les droits pour supprimer un encaissement.");
            return;
        }
        if (window.confirm("⚠️ Attention : Êtes-vous sûr de vouloir supprimer cet encaissement de la base de données ? Cette action est irréversible.")) {
            try {
                await deleteEncaissement(id);
                showMessage("success", "Encaissement supprimé avec succès.");
                loadData();
            } catch (error) {
                showMessage("error", error.response?.data?.message || "Erreur lors de la suppression.");
            }
        }
    };

    const availableAvoirsForClient = allAvoirs.filter(a => 
        a.clientName?.toLowerCase().trim() === selectedClient?.toLowerCase().trim()
    );

    const handleSubmit = async (ev) => {
        ev.preventDefault();

        const montantSaisi = parseFloat(amountToPay);
        if (!selectedClient) {
            showMessage("error", "Veuillez sélectionner un client valide.");
            return;
        }
        if (isNaN(montantSaisi) || montantSaisi <= 0) {
            showMessage("error", "Le montant doit être supérieur à zéro.");
            return;
        }

        if (editingId && !canEditAnywhere) {
            showMessage("error", "Action non autorisée. Vous ne pouvez pas modifier cet encaissement.");
            return;
        }

        if (paymentMode === 'Avoir') {
            const avoirExiste = availableAvoirsForClient.some(a => a.number === paymentReference);
            if (!avoirExiste) {
                showMessage("error", "Facture d'avoir inexistante ou invalide.");
                return; 
            }
        }

        const facturesConcernees = availableFactures.filter(f => selectedFactureIds.includes(f._id));
        let totalDuFactures = 0;
        
        if (isDetteInitialeSelected) {
            const client = allClientsObj.find(c => c.name === selectedClient);
            totalDuFactures += client ? client.initialDebt : 0;
        }
        totalDuFactures += facturesConcernees.reduce((sum, fac) => sum + (getFactureTotal(fac) - (fac.amountPaid || 0)), 0);

        let tropPercu = montantSaisi - totalDuFactures;

        if (!editingId && tropPercu > 0 && selectedFactureIds.length === 1 && !isDetteInitialeSelected && paymentMode !== 'Avoir') {
            const factureOrigine = facturesConcernees[0];
            try {
                const ligneOrigine = (factureOrigine.lines && factureOrigine.lines.length > 0) ? factureOrigine.lines[0] : {};
                const avoirData = {
                    type: 'avoir',
                    clientName: selectedClient,
                    date: receiptDate,
                    parentInvoice: factureOrigine.number,
                    lines: [
                        {
                            code: ligneOrigine.code || 'AVR',
                            name: ligneOrigine.name || `Régularisation facture ${factureOrigine.number}`,
                            details: prestationManuelle || `Trop perçu sur la facture ${factureOrigine.number}`,
                            price: tropPercu,
                            quantity: 1
                        }
                    ]
                };
                
                await createAvoir(avoirData);
                alert(`Facture d'avoir générée avec succès.\nTrop perçu : ${tropPercu} fcfa`);
            } catch (err) {
                const errorMessage = err.response?.data?.message || err.message;
                alert(`L'encaissement va se poursuivre, mais la génération de l'Avoir a échoué.\nRaison du serveur : ${errorMessage}`);
            }
        }

        const fData = new FormData();
        fData.append('clientName', selectedClient);
        fData.append('totalAmount', montantSaisi);
        fData.append('paymentMode', paymentMode);
        fData.append('paymentReference', paymentReference.trim()); 
        fData.append('date', receiptDate);
        fData.append('isDetteInitiale', isDetteInitialeSelected);
        fData.append('oldFactureReferences', oldFactureReferences.trim());
        fData.append('prestationManuelle', prestationManuelle);
        
        const selectedNumbers = facturesConcernees.map(f => f.number);
        fData.append('factureNumbers', JSON.stringify(selectedNumbers));
        
        if (proofFile) fData.append('proof', proofFile);

        try {
            if (editingId) {
                await updateEncaissement(editingId, { 
                    clientName: selectedClient,
                    totalAmount: montantSaisi, 
                    paymentMode, 
                    paymentReference: paymentReference.trim(), 
                    date: receiptDate, 
                    prestationManuelle,
                    isDetteInitiale: isDetteInitialeSelected,
                    oldFactureReferences: oldFactureReferences.trim(),
                    factureNumbers: JSON.stringify(selectedNumbers)
                });
            } else {
                await createEncaissement(fData);
            }
            showMessage("success", "Opération réussie. Caisse mise à jour !");
            resetForm(); loadData();
        } catch (err) { 
            const errorMsg = err.response?.data?.message || err.message || "Erreur inconnue";
            showMessage("error", `Erreur d'enregistrement : ${errorMsg}`); 
        }
    };

    const submitDecaissement = async (e) => {
        e.preventDefault();
        try {
            await createDecaissementSelection({
                encaissementIds: [decaisserData.id],
                date: decaisserData.date,
                nature: decaisserData.nature,
                reference: decaisserData.reference
            });
            showMessage("success", "Versement en banque validé avec succès !");
            setShowDecaisserModal(false);
            loadData();
        } catch (err) {
            showMessage("error", "Erreur lors du versement en banque.");
        }
    };

    let currentTotalDu = 0;
    if (isDetteInitialeSelected) {
        const client = allClientsObj.find(c => c.name === selectedClient);
        currentTotalDu += client ? client.initialDebt : 0;
    }
    const facturesConcernees = availableFactures.filter(f => selectedFactureIds.includes(f._id));
    currentTotalDu += facturesConcernees.reduce((sum, fac) => sum + (getFactureTotal(fac) - (fac.amountPaid || 0)), 0);
    
    const currentMontantSaisi = parseFloat(amountToPay) || 0;
    const currentTropPercu = currentMontantSaisi - currentTotalDu;

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaMoneyBillWave className="me-2 text-success"/> ENCAISSEMENTS & CAISSE
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>
                        Gestion des liquidités et des paiements clients
                    </p>
                </div>
            </div>

            {message && (
                <div className={`alert py-2 shadow-sm border-0 ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
                    {message.text}
                </div>
            )}

            {(!editingId || canEditAnywhere) && (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white py-3 fw-bold text-uppercase text-success" style={{ letterSpacing: '1px' }}>
                        {editingId ? "MODIFICATION DU PAIEMENT ET DES AFFECTATIONS" : "ENREGISTRER UN PAIEMENT"}
                    </div>
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 mb-3">
                                <div className="col-12 col-md-3 position-relative">
                                    <label className="small fw-bold mb-1 text-muted">CLIENT</label>
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text bg-white text-muted border-end-0"><FaSearch size={12}/></span>
                                        <input type="text" className="form-control border-start-0" value={clientSearch} 
                                            onChange={handleSearchChange} placeholder="Nom..." required/>
                                    </div>
                                    {showDropdown && (
                                        <div className="position-absolute shadow bg-white border rounded w-100 mt-1" style={{zIndex: 999, maxHeight: '200px', overflowY: 'auto'}}>
                                            {clientsWithDebtNames.filter(n => n.toLowerCase().includes(clientSearch.toLowerCase())).map((n, i) => (
                                                <div key={i} className="p-2 border-bottom cursor-pointer text-dark bg-light-hover" onMouseDown={() => handleSelectClient(n)} style={{cursor: 'pointer'}}>
                                                    <span className="fw-bold">{n}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="col-12 col-sm-6 col-md-2">
                                    <label className="small fw-bold mb-1 text-danger">MONTANT REÇU</label>
                                    <input type="number" className="form-control form-control-sm fw-bold text-end text-danger border-danger" style={{ backgroundColor: 'rgba(220, 53, 69, 0.05)' }}
                                        value={amountToPay === 0 ? '' : amountToPay} onChange={(e) => setAmountToPay(e.target.value)} placeholder="0" min="1" required/>
                                    
                                    {selectedClient && !editingId && currentTotalDu > 0 && currentMontantSaisi > 0 && currentTropPercu > 0 && paymentMode !== 'Avoir' && (
                                        <div className="small fw-bold mt-1 text-end text-warning">Trop perçu : {currentTropPercu.toLocaleString()} fcfa</div>
                                    )}
                                    {selectedClient && !editingId && currentTotalDu > 0 && currentMontantSaisi > 0 && currentTropPercu < 0 && (
                                        <div className="small fw-bold mt-1 text-end text-info">Solde restant : {Math.abs(currentTropPercu).toLocaleString()} fcfa</div>
                                    )}
                                </div>
                                
                                <div className="col-12 col-sm-6 col-md-2">
                                    <label className="small fw-bold mb-1 text-muted">MODE</label>
                                    <select className="form-select form-select-sm" value={paymentMode} onChange={handlePaymentModeChange}>
                                        <option value="Espèces">Espèces</option>
                                        <option value="Chèque">Chèque</option>
                                        <option value="Virement">Virement</option>
                                        <option value="Avoir">Avoir (Compensation)</option>
                                    </select>
                                </div>

                                <div className="col-12 col-md-3">
                                    <label className={`small fw-bold mb-1 ${paymentMode === 'Avoir' ? 'text-info' : 'text-muted'}`}>
                                        {paymentMode === 'Avoir' ? "CHOIX DE L'AVOIR" : 'RÉFÉRENCE'}
                                    </label>
                                    
                                    {paymentMode === 'Avoir' ? (
                                        <select 
                                            className="form-select form-select-sm border-info text-info"
                                            value={paymentReference} 
                                            onChange={(e) => {
                                                const selectedAvoirNum = e.target.value;
                                                setPaymentReference(selectedAvoirNum);
                                                const selectedAvoirObj = availableAvoirsForClient.find(a => a.number === selectedAvoirNum);
                                                if (selectedAvoirObj) setAmountToPay(selectedAvoirObj.totalGeneral);
                                            }} 
                                            required
                                        >
                                            <option value="">-- Sélectionnez un avoir --</option>
                                            {availableAvoirsForClient.length === 0 && (
                                                <option value="" disabled>Aucun avoir disponible</option>
                                            )}
                                            {availableAvoirsForClient.map(a => (
                                                <option key={a._id} value={a.number}>
                                                    {a.number} ({a.totalGeneral.toLocaleString()} F)
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input 
                                            type="text" 
                                            className="form-control form-control-sm" 
                                            value={paymentReference} 
                                            onChange={(e) => setPaymentReference(e.target.value)} 
                                            placeholder="N° de chèque, virement..." 
                                        />
                                    )}
                                </div>

                                <div className="col-12 col-sm-6 col-md-2">
                                    <label className="small fw-bold mb-1 text-muted">DATE</label>
                                    <input type="date" className="form-control form-control-sm" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} required/>
                                </div>

                                {paymentMode === 'Avoir' && (
                                    <div className="col-12 mt-2">
                                        <div className="alert alert-info py-2 small mb-0">
                                            <FaCheck className="me-2"/> <strong>Opération de compensation :</strong> Ce règlement soldera la facture mais n'impactera pas le solde liquide de la caisse (Trésorerie).
                                        </div>
                                    </div>
                                )}
                            </div>

                            {selectedClient && (
                                <div className="p-3 mb-3 border rounded bg-light">
                                    <div className="small fw-bold mb-3 text-primary">AFFECTATION DU RÈGLEMENT :</div>
                                    {editingId && <div className="alert alert-warning py-1 small mb-2">⚠️ Cochez ou décochez les éléments pour modifier la répartition du paiement.</div>}
                                    <div className="d-flex flex-column gap-3">
                                        {/* OPTION DETTE INITIALE MODIFIÉE */}
                                        {allClientsObj.find(c => c.name === selectedClient)?.initialDebt > 0 && (
                                            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                                                <div className="form-check m-0">
                                                    <input className="form-check-input border-danger" type="checkbox" id="detteInitialeCheck" checked={isDetteInitialeSelected} onChange={toggleDetteInitiale} />
                                                    <label className="form-check-label small fw-bold text-danger" htmlFor="detteInitialeCheck">
                                                        SOLDE INITIAL ({allClientsObj.find(c => c.name === selectedClient).initialDebt.toLocaleString()} F)
                                                    </label>
                                                </div>
                                                {isDetteInitialeSelected && (
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-sm" 
                                                        style={{ maxWidth: '300px' }} 
                                                        placeholder="N° de l'ancienne facture (Ex: FAC-2023/...)"
                                                        value={oldFactureReferences}
                                                        onChange={(e) => setOldFactureReferences(e.target.value)}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        <div className="d-flex flex-row flex-wrap gap-3 align-items-center">
                                            {availableFactures.map(f => (
                                                <div key={f._id} className="form-check m-0">
                                                    <input className="form-check-input" type="checkbox" id={`factureCheck-${f._id}`} checked={selectedFactureIds.includes(f._id)} onChange={() => toggleFacture(f)} />
                                                    <label className="form-check-label small text-dark fw-bold" htmlFor={`factureCheck-${f._id}`}>
                                                        {f.number} <span className="text-muted fw-normal">(Initial: {getFactureTotal(f).toLocaleString()} F)</span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>

                                        {availableFactures.length === 0 && !(allClientsObj.find(c => c.name === selectedClient)?.initialDebt > 0) && (
                                            <div className="text-muted small fst-italic">Aucune facture en attente pour ce client.</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="row g-2 mb-4">
                                <div className="col-12 col-md-8">
                                    <label className="small fw-bold mb-1 text-muted">LIBELLÉ / OBJET DU PAIEMENT</label>
                                    <input type="text" className="form-control form-control-sm" value={prestationManuelle} onChange={(e) => setPrestationManuelle(e.target.value)} placeholder="Ex: Règlement de la facture N°..." />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="small fw-bold mb-1 text-muted">PREUVE (IMAGE/PDF)</label>
                                    <input type="file" className="form-control form-control-sm" onChange={(e) => setProofFile(e.target.files[0])} accept=".pdf, image/*" />
                                </div>
                            </div>

                            <div className="d-flex flex-column flex-sm-row justify-content-end border-top pt-3 gap-2 mt-3">
                                {editingId && (
                                    <button type="button" className="btn btn-outline-secondary fw-bold px-3" onClick={resetForm}>
                                        ANNULER
                                    </button>
                                )}
                                <button className="btn btn-success fw-bold px-4 shadow-sm" type="submit">
                                    <FaCheck className="me-2"/> {editingId ? 'METTRE À JOUR' : "VALIDER L'ENCAISSEMENT"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
                    <span className="fw-bold text-dark text-uppercase" style={{ letterSpacing: '1px' }}>
                        <FaHistory className="me-2 text-success"/> JOURNAL DE CAISSE
                    </span>
                    <div className="input-group input-group-sm w-100 w-sm-auto" style={{maxWidth: '300px'}}>
                        <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                        <input className="form-control border-start-0" placeholder="Rechercher..." onChange={(e) => setSearchHistory(e.target.value)} />
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3 border-bottom-0">N° Reçu</th>
                                <th className="py-3 border-bottom-0 text-center">Date</th>
                                <th className="py-3 border-bottom-0">Client</th>
                                <th className="py-3 border-bottom-0 text-end">Montant</th>
                                <th className="py-3 border-bottom-0 text-center">Mode</th>
                                <th className="py-3 border-bottom-0 text-center">État</th>
                                <th className="pe-3 py-3 text-end border-bottom-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.filter(e => e.clientName.toLowerCase().includes(searchHistory.toLowerCase()) || e.number.includes(searchHistory)).map(e => (
                                <tr key={e._id}>
                                    <td className="ps-3 fw-bold text-success">{e.number}</td>
                                    <td className="text-muted small text-center">{new Date(e.dateEncaissement || e.date).toLocaleDateString()}</td>
                                    <td className="fw-bold text-dark text-wrap" style={{minWidth: '150px'}}>{e.clientName}</td>
                                    <td className="fw-bold text-dark text-end">{e.totalAmount?.toLocaleString()} F</td>
                                    <td className="text-center">
                                        {e.paymentMode === 'Avoir' ? <span className="fw-bold text-info">AVOIR</span> : <span className="text-muted">{e.paymentMode}</span>}
                                    </td>
                                    <td className="text-center">
                                        <span className={`badge rounded-1 px-2 py-1 border ${
                                            e.paymentMode === 'Avoir' ? 'bg-info text-info bg-opacity-10 border-info' : 
                                            (e.isDecaisser ? 'bg-success text-success bg-opacity-10 border-success' : 'bg-warning text-warning-emphasis bg-opacity-10 border-warning')
                                        }`}>
                                            {e.paymentMode === 'Avoir' ? 'COMPENSÉ' : (e.isDecaisser ? 'EN BANQUE' : 'EN CAISSE')}
                                        </span>
                                    </td>
                                    <td className="text-end pe-3">
                                        
                                        <button 
                                            className="btn btn-sm btn-outline-info rounded-1 me-1 py-1 px-2"
                                            title="Voir les détails" 
                                            onClick={() => setDetailsModalData(e)}
                                        >
                                            <FaEye size={14}/>
                                        </button>

                                        <button 
                                            className="btn btn-sm btn-outline-danger rounded-1 me-1 py-1 px-2"
                                            title="Télécharger PDF" 
                                            onClick={() => handleDownloadPDF(e._id, e.number)}
                                            disabled={downloadingPdfId === e._id}
                                        >
                                            {downloadingPdfId === e._id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <FaFilePdf size={14}/>}
                                        </button>
                                        
                                        {canEditAnywhere && (
                                            <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" title="Modifier" onClick={() => startEdit(e)}>
                                                <FaEdit size={14}/>
                                            </button>
                                        )}

                                        {canEditAnywhere && (
                                            <button className="btn btn-sm btn-outline-secondary rounded-1 me-1 py-1 px-2" title="Supprimer" onClick={() => handleDelete(e._id)}>
                                                <FaTrash size={14}/>
                                            </button>
                                        )}
                                        
                                        {!e.isDecaisser && e.paymentMode !== 'Avoir' && !isKbOuattara && (
                                            <button className="btn btn-sm btn-outline-success rounded-1 py-1 px-2 ms-1" title="Verser en banque" onClick={() => {
                                                setDecaisserData({
                                                    id: e._id,
                                                    nature: `Décaissement ${e.paymentMode}`,
                                                    reference: e.number,
                                                    date: new Date().toISOString().split('T')[0]
                                                });
                                                setShowDecaisserModal(true);
                                            }}>
                                                <FaUniversity size={14}/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {detailsModalData && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-light py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-uppercase text-info" style={{ letterSpacing: '1px' }}>
                                    <FaEye className="me-2"/> DÉTAILS DE L'ENCAISSEMENT
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setDetailsModalData(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row mb-4">
                                    <div className="col-12 col-md-4 mb-3">
                                        <p className="mb-1 small text-muted fw-bold">N° REÇU</p>
                                        <p className="fw-bold text-dark fs-5 mb-0">{detailsModalData.number}</p>
                                    </div>
                                    <div className="col-12 col-md-4 mb-3">
                                        <p className="mb-1 small text-muted fw-bold">DATE</p>
                                        <p className="fw-bold text-dark mb-0">{new Date(detailsModalData.dateEncaissement || detailsModalData.date).toLocaleDateString()}</p>
                                    </div>
                                    <div className="col-12 col-md-4 mb-3">
                                        <p className="mb-1 small text-muted fw-bold">STATUT</p>
                                        <span className={`badge rounded-1 px-2 py-1 border ${
                                            detailsModalData.paymentMode === 'Avoir' ? 'bg-info text-info bg-opacity-10 border-info' : 
                                            (detailsModalData.isDecaisser ? 'bg-success text-success bg-opacity-10 border-success' : 'bg-warning text-warning-emphasis bg-opacity-10 border-warning')
                                        }`}>
                                            {detailsModalData.paymentMode === 'Avoir' ? 'COMPENSÉ' : (detailsModalData.isDecaisser ? 'EN BANQUE' : 'EN CAISSE')}
                                        </span>
                                    </div>
                                    <div className="col-12 col-md-4 mb-3">
                                        <p className="mb-1 small text-muted fw-bold">CLIENT</p>
                                        <p className="fw-bold text-dark mb-0">{detailsModalData.clientName}</p>
                                    </div>
                                    <div className="col-12 col-md-4 mb-3">
                                        <p className="mb-1 small text-muted fw-bold">MONTANT</p>
                                        <p className="fw-bold text-success fs-5 mb-0">{detailsModalData.totalAmount?.toLocaleString()} F</p>
                                    </div>
                                    <div className="col-12 col-md-4 mb-3">
                                        <p className="mb-1 small text-muted fw-bold">MODE DE PAIEMENT</p>
                                        <p className="fw-bold text-dark mb-0">
                                            {detailsModalData.paymentMode}
                                            {detailsModalData.paymentReference && <span className="ms-1 text-muted fw-normal">(Réf: {detailsModalData.paymentReference})</span>}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-light p-3 rounded border">
                                    <h6 className="fw-bold text-primary border-bottom pb-2 mb-3" style={{ fontSize: '13px' }}>AFFECTATION / OBJET</h6>
                                    
                                    <ul className="list-group list-group-flush bg-transparent">
                                        {detailsModalData.isDetteInitiale && (
                                            <li className="list-group-item bg-transparent px-0 text-danger fw-bold py-1 border-0">
                                                <FaCheck className="me-2"/> Règlement du Solde Initial (Créance antérieure) 
                                                {detailsModalData.oldFactureReferences && <span className="ms-2 text-dark fw-normal">Réf : {detailsModalData.oldFactureReferences}</span>}
                                            </li>
                                        )}
                                        {detailsModalData.factureNumbers && detailsModalData.factureNumbers.length > 0 && (
                                            <li className="list-group-item bg-transparent px-0 py-1 border-0 text-dark">
                                                <strong>Factures ciblées :</strong> <span className="text-primary fw-bold">{detailsModalData.factureNumbers.join(' / ')}</span>
                                            </li>
                                        )}
                                        {detailsModalData.prestationManuelle && (
                                            <li className="list-group-item bg-transparent px-0 py-1 border-0 text-dark">
                                                <strong>Libellé / Note :</strong> {detailsModalData.prestationManuelle}
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-top-0 d-flex justify-content-end">
                                <button type="button" className="btn btn-secondary px-4 fw-bold" onClick={() => setDetailsModalData(null)}>FERMER</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {showDecaisserModal && !isKbOuattara && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-light py-3 border-bottom">
                                <h6 className="mb-0 fw-bold text-uppercase text-success" style={{ letterSpacing: '1px' }}>
                                    <FaUniversity className="me-2"/> VERSER EN BANQUE
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowDecaisserModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <form onSubmit={submitDecaissement}>
                                    <div className="mb-3">
                                        <label className="small fw-bold mb-1 text-muted">DATE DU VERSEMENT</label>
                                        <input 
                                            type="date" 
                                            className="form-control" 
                                            value={decaisserData.date} 
                                            onChange={(e) => setDecaisserData({...decaisserData, date: e.target.value})} 
                                            required 
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="small fw-bold mb-1 text-muted">NATURE / OBJET</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={decaisserData.nature} 
                                            onChange={(e) => setDecaisserData({...decaisserData, nature: e.target.value})} 
                                            required 
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="small fw-bold mb-1 text-muted">RÉFÉRENCE (Bordereau, etc.)</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={decaisserData.reference} 
                                            onChange={(e) => setDecaisserData({...decaisserData, reference: e.target.value})} 
                                        />
                                    </div>
                                    <div className="d-flex justify-content-end gap-2 border-top pt-3">
                                        <button type="button" className="btn btn-secondary px-3 fw-bold" onClick={() => setShowDecaisserModal(false)}>
                                            ANNULER
                                        </button>
                                        <button type="submit" className="btn btn-success px-4 fw-bold">
                                            <FaCheck className="me-2"/> VALIDER
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EncaissementPage;